import { useEffect, useRef, useState } from "react";
import { IoMdClose } from "react-icons/io";
import { IoSearch } from "react-icons/io5";
import { MeetingService } from "../../services/MeetingService";
import { toast } from "react-toastify";
import useModalCloseHook from "../../hooks/useModalCloseHook";
import Button from "@mui/material/Button";
import ViewMeetingDetails from "./ViewMeetingDetails";
import Participants from "./Participants";

export default function AddMemberModal({ isOpen, onClose, meetingId }) {
  const modalRef = useRef(null);

  useModalCloseHook(modalRef, onClose);
  const [activeTab, setActiveTab] = useState("add");
  const [searchTerm, setSearchTerm] = useState("");
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [employees, setEmployees] = useState([]);

  const handleSelect = (member) => {
    if (!selectedMembers.some((m) => m.id === member.id)) {
      setSelectedMembers([...selectedMembers, member]);
    }
    setSearchTerm("");
    setIsSelectorOpen(false);
  };

  const handleRemove = (id) => {
    setSelectedMembers(selectedMembers.filter((m) => m.id !== id));
  };

  const filteredMembers = employees.filter((emp) =>
    emp.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddMembers = async () => {
    if (selectedMembers.length === 0) {
      toast.error("Please select at least one member.");
      return;
    }

    const participantIds = selectedMembers.map((member) => member.id);

    try {
      await MeetingService.addParticipants(meetingId, participantIds);
      toast.success("Participants added successfully.");
      setSelectedMembers([]);
      onClose();
    } catch (error) {
      toast.error(error?.message || "Failed to add participants");
    }
  };

  useEffect(() => {
    if (isOpen) {
      const fetchEligibleEmployees = async () => {
        try {
          const data = await MeetingService.getEligibleEmployees();
          setEmployees(data);
        } catch (error) {
          toast.error(error?.message || "Failed to get employees list");
        }
      };

      fetchEligibleEmployees();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4">
      <div
        ref={modalRef}
        className="bg-white w-full max-w-lg rounded-2xl shadow-xl p-6 relative animate-fadeIn"
      >
        {/* Header */}
        <div className="flex justify-between items-center border-b border-gray-200 pb-3 mb-4">
          <h2 className="text-2xl font-semibold text-gray-800">Meeting Info</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 transition"
          >
            <IoMdClose className="text-2xl text-gray-500 hover:text-gray-800" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-4">
          {["add", "view"].map((tab) => (
            <button
              key={tab}
              className={`px-4 py-2 text-sm font-medium transition ${
                activeTab === tab
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === "add" ? "Add Members" : "View Details"}
            </button>
          ))}
        </div>

        {/* ADD MEMBERS TAB */}
        {activeTab === "add" && (
          <>
            <Participants meetingId={meetingId} />

            {/* Selected Members */}
            {selectedMembers.length > 0 ? (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  Selected Members
                </h3>
                <div className="flex flex-wrap gap-2">
                  {selectedMembers.map((member) => (
                    <div
                      key={member.id}
                      className="bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full flex items-center gap-1"
                    >
                      {member.name}
                      <button
                        onClick={() => handleRemove(member.id)}
                        className="hover:text-red-500 transition"
                      >
                        <IoMdClose className="text-xs" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-red-500 mb-4">
                No members selected yet to add
              </p>
            )}

            {/* Search Input */}
            <div className="relative mb-3">
              <label className="text-sm text-gray-700 font-semibold block mb-1">
                Add Participants
              </label>
              <div className="flex items-center border border-gray-300 rounded-md overflow-hidden">
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchTerm(value);
                    setIsSelectorOpen(value.trim() !== "");
                  }}
                  onFocus={() => {
                    if (searchTerm.trim()) setIsSelectorOpen(true);
                  }}
                  className="w-full px-4 py-2 text-sm focus:outline-none"
                />
                <div
                  onClick={() => setIsSelectorOpen((prev) => !prev)}
                  className="px-3 cursor-pointer hover:bg-gray-100 transition"
                >
                  <IoSearch className="text-gray-600" />
                </div>
              </div>
            </div>

            {/* Search Dropdown */}
            {isSelectorOpen && (
              <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-1 mb-4 bg-white shadow-sm">
                {filteredMembers.length > 0 ? (
                  filteredMembers.map((emp) => (
                    <div
                      key={emp.id}
                      onClick={() => handleSelect(emp)}
                      className="cursor-pointer hover:bg-gray-100 px-3 py-1 rounded text-sm text-gray-800"
                    >
                      {emp.name}
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No employees found</p>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="flex justify-end pt-4 border-t border-gray-200 mt-4 gap-3">
              <Button
                onClick={onClose}
                variant="outlined"
                className="!text-gray-700 !bg-white !capitalize !w-[100px] !font-medium !border !border-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddMembers}
                variant="contained"
                className="btn-gradient !capitalize !w-[100px] !font-semibold !text-white"
              >
                Add
              </Button>
            </div>
          </>
        )}

        {/* VIEW DETAILS TAB */}
        {activeTab === "view" && (
          <div className="min-h-[230px]">
            <ViewMeetingDetails meetingId={meetingId} />
          </div>
        )}
      </div>
    </div>
  );
}
